import { cn } from '@/lib/utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { AnimatePresence, motion } from 'framer-motion';
import { ButtonHTMLAttributes, FC, HTMLAttributes, PropsWithChildren } from 'react';
import { toast } from 'sonner';
import { Button } from '../form/Button';
import { useSelection } from '@/services/selection/useSelectionProvider';

interface SelectionProps {
  id: string;
}

interface SelectAllProps {
  ids: string[];
}

export const SelectionContainer: FC<PropsWithChildren<SelectionProps & HTMLAttributes<HTMLDivElement>>> = ({
  id,
  children,
  className,
  ...props
}) => {
  const [selected, addToSelection, removeFromSelection] = useSelection(state => [
    state.selected,
    state.addToSelection,
    state.removeFromSelection
  ]);
  const isSelected = selected.has(id);

  return (
    <Slot
      {...props}
      className={cn('rounded-lg relative group p-2', isSelected && 'outline-2 outline outline-primary-700', className)}
    >
      <Slottable>
        <div
          onClick={() => {
            if (isSelected) {
              removeFromSelection(id);
            } else {
              addToSelection(id);
            }
          }}
          className="cursor-pointer"
        >
          {children}
        </div>
      </Slottable>
    </Slot>
  );
};

export const SelectAllButton: FC<SelectAllProps & ButtonHTMLAttributes<HTMLButtonElement>> = ({ ids, ...props }) => {
  const [addToSelection] = useSelection(state => [state.addToSelection]);

  return (
    <Button
      onClick={e => {
        e.stopPropagation();
        e.bubbles = false;
        if (ids.length === 0) {
          toast('There is nothing to select');
          return;
        }
        ids.forEach(id => addToSelection(id));
      }}
      {...props}
    >
      Select All
    </Button>
  );
};

export const SelectionBar: FC<PropsWithChildren<{ selectedItemIds?: string[] }>> = ({ children }) => {
  const selected = useSelection(state => state.selected);
  const selectedItemIds = Array.from(selected);

  return (
    <AnimatePresence>
      {selectedItemIds.length > 0 && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 16 }}
          exit={{ y: 100 }}
          className="bg-gray-50 fixed bottom-0 inset-x-0 p-4 pb-8 mx-6 border border-b-0 border-gray-200 rounded-t-lg flex gap-2 items-center z-20"
        >
          <div className="flex-1 flex gap-1 flex-wrap sm:flex-nowrap">
            <span className="font-semibold text-sm">{selectedItemIds.length}</span>
            <span className="text-sm text-gray-500">selected asset{selectedItemIds.length === 1 ? '' : 's'}</span>
          </div>
          <div className="flex justify-end gap-2 flex-wrap sm:flex-nowrap">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
